import React, { useEffect, useState, createRef, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { Chart, Title, CommonSeriesSettings, Legend, Series, Tooltip, ValueAxis, Export, Label, Font, Connector } from "devextreme-react/chart";
import { dataService } from "../../../../Services/data/data.service";
import { controlTypes, valueTypeCodes, externalApiTypes } from "../../../../Config/constants";
import { parse } from "date-fns";

export default function OutputChart({ allMeasurements = [], compareMeasurements, currency, loading, ...props }) {
  const classes = useStyles();

  const [isCompare, setIsCompare] = useState(false);

  const [allUnfilteredMeasurements, setAllUnfilteredMeasurements] = useState();

  const [currentDataSource, setCurrentDataSource] = useState([]);
  const [compareDataSource, setCompareDataSource] = useState([]);

  const [valueTypes, setValueTypes] = useState([]);
  const [dataSource, setDataSource] = useState(null);
  const [emptyState, setEmptyState] = useState(false);
  const [emptyStateDataSource] = useState([
    {
      state: "Station 1",
      VA: 2,
      type: 1,
      typeName: "VA",
      title: "timer 1",
      color: "#faead9",
      measurements: 3,
    },
    {
      state: "Station 1",
      W: 1,
      type: 3,
      typeName: "W",
      title: "timer 2",
      color: "#c34141",
      measurements: 1,
    },
    {
      state: "Station 1",
      NVA: 1,
      type: 2,
      typeName: "NVA",
      title: "timer 3",
      color: "#25b513",
      measurements: 1,
    },
    {
      state: "Station 2",
      VA: 1,
      type: 1,
      typeName: "VA",
      title: "Value added 1",
      color: "#9aff48",
      measurements: 3,
    },
    {
      state: "Station 2",
      NVA: 1,
      type: 2,
      typeName: "NVA",
      title: "Non value added 1",
      color: "#fccc72",
      measurements: 7,
    },
    {
      state: "Station 2",
      W: 1,
      type: 3,
      typeName: "W",
      title: "Waste 1",
      color: "#ff6868",
      measurements: 5,
    },
  ]);

  useEffect(() => {
    if (allMeasurements !== null && allMeasurements.length > 0 && allMeasurements !== allUnfilteredMeasurements) {
      setAllUnfilteredMeasurements(allMeasurements);
      let parsedMeasurements = parseMeasurementRows(allMeasurements);
      setDataSource(parsedMeasurements);
      setCurrentDataSource(parsedMeasurements);
    } else {
      setDataSource([]);
      setEmptyState(true);
    }
  }, [allMeasurements]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (compareMeasurements && compareMeasurements.length > 0) {
      setIsCompare(true);
      let parsedCompareMeasurements = parseMeasurementRows(compareMeasurements, true);
      setCompareDataSource(parsedCompareMeasurements);
    } else {
      setIsCompare(false);
    }
  }, [compareMeasurements]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to merge current and comparison data for side-by-side stacks
  function mergeForSideBySideStacks(currentData, comparisonData) {
    const tag = (data, datasetName) =>
      data.map((item) => ({
        ...item,
        dataset: datasetName,
      }));

    return [...tag(currentData, "Current"), ...tag(comparisonData, "Comparison")];
  }

  // Function to convert hex color to RGBA with alpha
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Move useMemo to top level
  const mergedData = useMemo(() => {
    if (isCompare && currentDataSource !== null && currentDataSource.length > 0 && compareDataSource !== null && compareDataSource.length > 0) {
      return mergeForSideBySideStacks(currentDataSource, compareDataSource);
    }
    return null;
  }, [currentDataSource, compareDataSource]);

  useEffect(() => {
    if (mergedData) {
      setDataSource(mergedData);
      setEmptyState(false);
    } else if (currentDataSource && currentDataSource.length > 0) {
      setDataSource(currentDataSource);
      setEmptyState(false);
    } else if (!currentDataSource || currentDataSource.length === 0) {
      setDataSource([]);
      setEmptyState(true);
    }
  }, [currentDataSource, compareDataSource, mergedData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dataService.getValueTypes(props.Login.userid).then((result) => {
      if (result) {
        var val = result.sort((a, b) => a.name.localeCompare(b.name));
        setValueTypes(val);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const parseMeasurementRows = (measurementRows, isCompare = false) => {
    if (measurementRows === null || measurementRows.length === 0) {
      setEmptyState(true);
      return [];
    } else {
      setEmptyState(false);
    }

    // Initialize an object to hold the parsed data for each project
    let projectData = {};
    // Loop through each measurement row in the array
    measurementRows.forEach((row) => {
      // Only process rows where "checked" is true
      if (!row.checked) return;

      if (
        row.typeId === controlTypes.COUNTER ||
        row.typeId === controlTypes.DATALIST ||
        row.typeId === controlTypes.FREETEXT ||
        row.typeId === controlTypes.SCANNER ||
        (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.DATA) ||
        (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.NUMERIC)
      ) {
        const projectId = row.projectId;
        const detailId = row.detailId;
        let eventPrice = row.eventPrice;
        let projectedEventPrice = row.projectedEventPrice;

        // Initialize data for this project if not already done
        if (!projectData[projectId]) {
          projectData[projectId] = {
            state: row.projectName + " - " + row.resourceName,
            values: {},
          };
        }

        // Check if this detailId has already been processed for this project
        if (!projectData[projectId].values[detailId]) {
          const baseTypeName = row.valueTypeId === valueTypeCodes.VA ? "VA" : row.valueTypeId === valueTypeCodes.NVA ? "NVA" : row.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN";
          projectData[projectId].values[detailId] = {
            typeId: row.valueTypeId,
            title: row.description,
            color: row.color,
            typeName: isCompare ? baseTypeName + "compare" : baseTypeName,
            measurementCounter: 0,
            totalPrice: 0,
            projectedTotalPrice: 0,
          };
        }

        // Check if the event price should be added or subtracted
        eventPrice = row.isProfit === row.isNegative ? -eventPrice : eventPrice;
        projectedEventPrice = row.isProfit === row.isNegative ? -projectedEventPrice : projectedEventPrice;

        // Update the total price and measurement counter for this detail
        projectData[projectId].values[detailId].totalPrice += eventPrice;
        projectData[projectId].values[detailId].projectedTotalPrice += projectedEventPrice;
        projectData[projectId].values[detailId].measurementCounter += 1;
      }
    });

    // Transform the data into the required output format
    const parsedOutput = [];
    Object.values(projectData).forEach((project) => {
      Object.values(project.values).forEach((detail) => {
        parsedOutput.push({
          state: project.state,
          [detail.typeName]: detail.measurementCounter, // Dynamic key based on typeName
          type: detail.typeId,
          typeName: detail.typeName,
          title: detail.title,
          color: detail.color,
          measurements: detail.measurementCounter,
          totalPrice: detail.totalPrice,
          projectedTotalPrice: detail.projectedTotalPrice,
          dataset: isCompare ? "Comparison" : "Current", // Add dataset tag
        });
      });
    });

    if (parsedOutput.length === 0 || parsedOutput.every((output) => output.typeName === "UNKNOWN")) {
      setEmptyState(true);
      return [];
    }

    return parsedOutput;
  };

  function customizeTooltip(pointInfo) {
    let percent = pointInfo.percentText;

    let type = pointInfo.seriesName;
    let title = pointInfo.point.data.title;
    let counter = pointInfo.point.data.measurements;
    let totalPrice = pointInfo.point.data.totalPrice;
    let projectedTotalPrice = pointInfo.point.data.projectedTotalPrice;

    let text = "<div>";
    text += "<div><b>Title: </b> " + title + "</div>";
    text += "<div><b>Percentage: </b>" + percent + "</div>";
    text += "<div><b>Type: </b>" + type + "</div>";
    text += "<div><b>Amount of measurements: </b>" + counter + "</div></div>";
    if (totalPrice != null) {
      const currencySymbol = currency?.symbol ?? "";
      text += "<div><b>Total Price: </b>" + currencySymbol + totalPrice.toFixed(2) + "</div>";
      if (projectedTotalPrice != null && projectedTotalPrice !== 0 && Math.abs(projectedTotalPrice - totalPrice) > 0.01) text += "<div><b>Simulated total price: </b>" + currencySymbol + projectedTotalPrice.toFixed(2) + "</div>";
    }

    return { html: text };
  }
  // Function to generate custom filename
  const generateFileName = () => {
    const formattedDate = getFormattedDate();
    const chartTitle = "Output"; // You can customize this or make it dynamic
    if (props.graphConfigurationName !== "" && props.graphConfigurationName !== undefined && props.graphConfigurationName !== null) {
      return `${chartTitle}_${props.graphConfigurationName}_${formattedDate}`;
    } else {
      return `${chartTitle}_${formattedDate}`;
    }
  };

  const getFormattedDate = () => {
    const date = new Date();
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    // Get date in MM-DD-YYYY format
    return date.toLocaleDateString(undefined, options).replace(/\//g, "-");
  };

  const chartRef = createRef();

  function onPointClick({ target: point }) {
    if (point.isSelected()) {
      point.clearSelection();
    } else {
      point.select();
    }
  }

  function onLegendClick({ target: series }) {
    if (series.isVisible()) {
      series.hide();
    } else {
      series.show();
    }
  }

  function customizeText(info) {
    return info.value + " Measurement" + (info.value > 1 ? "s" : "");
  }

  return (
    <>
      {emptyState && (
        <div className={classes.emptyStateContainer}>
          <div className={classes.emptyStateCard}>
            <p className={classes.emptyStateCard__title}>No measurements</p>
            <p className={classes.emptyStateCard__subtitle}>There are no measurements to display</p>
          </div>
        </div>
      )}
      {loading && (
        <div className={classes.loadingOverlay}>
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner}></div>
            <p className={classes.loadingCard__title}>Loading chart...</p>
          </div>
        </div>
      )}
      <div className={classes.chartBox}>
        {!emptyState && dataSource !== null && dataSource.length > 0 && (
          <Chart
            id="chart"
            title="Output"
            key={`chart`} // Force re-render
            className={classes.chart}
            onPointClick={onPointClick}
            onLegendClick={onLegendClick}
            ref={chartRef}
            dataSource={dataSource}
          >
            <CommonSeriesSettings
              argumentField="state"
              type="stackedbar"
              barWidth={96}
              border={{
                visible: true, // Enable border
                width: 0.3, // Width of the border
                color: "white", // Color of the border (adjust as needed)
                dashStyle: "solid",
              }}
            />
            {!isCompare &&
              valueTypes !== null &&
              valueTypes !== undefined &&
              valueTypes.map((type) => {
                const filteredData = dataSource.filter((item) => item !== undefined && item.type !== undefined && item.type !== null).filter((item) => item.type === type.id);
                if (filteredData.length > 0) {
                  return (
                    <Series key={`${type.code}-${type.name}`} valueField={type.code} argumentField="state" name={type.name} color={type.colorCode} dataSource={filteredData} tagField="typeName">
                      <Label
                        visible={props.labelVisible}
                        position="inside"
                        horizontalOffset={0} // Optional fine-tuning
                        verticalOffset={0} // Optional fine-tuning
                        customizeText={customizeText}
                        overlappingBehavior={"Hide"}
                      >
                        <Font size={10} />
                        <Connector visible={true} width={0.5} />
                      </Label>
                    </Series>
                  );
                }
                return null;
              })}
            {/* ────────── COMPARISON  (LEFT stack) ────────── */}
            {isCompare &&
              valueTypes?.map((type) => {
                return (
                  <Series
                    key={`cmp-${type.code}`}
                    valueField={type.code + "compare"}
                    argumentField="state"
                    name={`${type.name} (Comparison)`}
                    color={hexToRgba(type.colorCode, 0.64)}
                    stack="Comparison" // ← all three go in the same stack
                  />
                );
              })}

            {/* ─────────── CURRENT  (RIGHT stack) ─────────── */}
            {isCompare &&
              valueTypes?.map((type) => {
                return (
                  <Series
                    key={`cur-${type.code}`}
                    valueField={type.code}
                    argumentField="state"
                    name={`${type.name} (Current)`}
                    color={type.colorCode}
                    stack="Current" // ← right‑hand stack
                  >
                    <Label visible={props.labelVisible} position="inside" customizeText={customizeText} overlappingBehavior="none">
                      <Font size={10} />
                      <Connector visible width={0.5} />
                    </Label>
                  </Series>
                );
              })}
            <ValueAxis position="left" tickInterval={1}>
              <Title text="Measurements" />
            </ValueAxis>
            <Legend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="top" />
            <Export
              enabled={true}
              fileName={generateFileName()} // Set your custom filename here
            />
            <Tooltip enabled={true} location="edge" customizeTooltip={customizeTooltip} />
          </Chart>
        )}
      </div>
    </>
  );
}

const useStyles = makeStyles(() => ({
  chart: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  chartBox: {
    padding: "10px",
  },

  emptyStateContainer: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "998",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",

    textAlign: "center",

    margin: "0px",
  },

  loadingOverlay: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "999",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  loadingCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  loadingCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginTop: "16px",
  },

  loadingSpinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));
